'use client'

import { Box, IconProps as MuiIconProps } from '@mui/material'
import { css, styled, SxProps } from '@mui/material/styles'
import { BoxProps } from '@mui/system'
import { memo } from 'react'

import * as icons from './assets'

export const iconMap = { ...icons }

export const sizeMap = {
  sm: '16px',
  md: '22px',
  lg: '28px',
  xl: '34px',
  xxl: '40px',
  xxxl: '46px',
  xxxxl: '52px',
}

const rotationMap = {
  '0': 0,
  '90': 90,
  '180': 180,
  '270': 270,
}

export type IconProps = {
  /**
   * The icon to render (React Component wrapping an SVG).
   */
  readonly icon: keyof typeof iconMap
  /**
   * The icon size — key of `sm`, `md`, `lg` or `xl`.
   */
  readonly size?: keyof typeof sizeMap | string
  /**
   * The angle of rotation in degrees — key of `90`, `180` or `270`.
   */
  readonly rotation?: keyof typeof rotationMap
  /**
   * The color of the icon using a key from the palette e.g. `primary`.  Defaults to the current inherited text color.
   */
  readonly color?: MuiIconProps['color']
  /**
   * Pass through MUI's sx prop
   */
  readonly sx?: SxProps
  /**
   * Pass through MUI's className
   */
  readonly className?: BoxProps['className']
  /**
   * Accessible label for the icon. When provided, the icon will be announced by screen readers.
   * When omitted, the icon is treated as decorative.
   */
  readonly ariaLabel?: string
  /**
   * When true, the icon is hidden from assistive technology.
   */
  readonly ariaHidden?: boolean
}

const StyledIconWrapper = styled(Box)<{
  rotation: number
  size: string
  color?: MuiIconProps['color']
  shouldTransition?: boolean
}>`
  ${({ size, rotation }) => {
    return css`
      ${rotation ? `transform: rotate(${rotation}deg);` : ''};
      width: ${size};
      height: ${size};
      transition: transform 0.3s ease;
      flex-shrink: 0;
      svg {
        display: block;
        width: ${size};
        height: ${size};
      }
    `
  }}
`

export const Icon = memo(
  ({
    icon,
    size: sizeProp = 'md',
    rotation,
    color,
    sx,
    className,
    ariaLabel,
    ariaHidden,
  }: IconProps) => {
    const IconMap = iconMap[icon]
    const label = `${icon.toLowerCase()}-icon`
    const size = sizeMap[sizeProp as keyof typeof sizeMap] ?? sizeProp

    return (
      <StyledIconWrapper
        color={color}
        size={size}
        rotation={rotation ? rotationMap[rotation] : 0}
        role={ariaHidden ? undefined : ariaLabel ? 'img' : 'presentation'}
        aria-label={ariaHidden ? undefined : ariaLabel}
        aria-hidden={ariaHidden || undefined}
        data-testid={label}
        sx={{ ...sx, color: color ? `${color}.main` : 'inherit' }}
        className={className}
      >
        <IconMap />
      </StyledIconWrapper>
    )
  },
)

export type IconType = keyof typeof iconMap
